import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  PointOfSale as PointOfSaleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Circle as OnlineDotIcon,
} from '@mui/icons-material';
import pavoApiService, { PavoDevice } from '../../../../../../services/pavoApiService';
import { useTranslation } from 'react-i18next';

interface PavoDevicesTabProps {
  screenId: number | null;
  companyId: string | null;
  branchId?: string | null;
  screenName?: string;
  screenType: 'quicksale' | 'restaurant';
}

const PavoDevicesTab: React.FC<PavoDevicesTabProps> = ({
  screenId,
  companyId,
  branchId,
  screenName,
  screenType,
}) => {
  const { t } = useTranslation('salesSystem');
  const isMobile = useMediaQuery('(max-width: 1499px)');

  const [devices, setDevices] = useState<PavoDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ekle
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [adding, setAdding] = useState(false);

  // Sil
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState<PavoDevice | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const deviceMode = screenType === 'restaurant' ? 'pavo_restaurant' : 'pavo_quicksale';
  const screenLabel = screenType === 'restaurant' ? t('restaurantScreen.restaurant') : t('quickSaleSettingsModal.titles.quickSale');

  const loadDevices = useCallback(async () => {
    if (!companyId || !screenId) return;

    setLoading(true);
    setError(null);
    try {
      const list = await pavoApiService.getDevicesByScreen(companyId, screenId, screenType);
      setDevices(list);
    } catch (err: any) {
      console.error('Pavo cihaz listesi yükleme hatası:', err);
      setError(err?.message || t('pavoDevicesTab.errors.listLoadFailed'));
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, screenId, screenType]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleAddDevice = async () => {
    if (!companyId || !screenId) return;

    const trimmed = newDeviceName.trim();
    if (!trimmed) {
      setError(t('pavoDevicesTab.errors.deviceNameRequired'));
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const result = await pavoApiService.addPavoDevice(
        companyId,
        branchId || '',
        deviceMode,
        trimmed,
        screenId,
      );

      setAddDialogOpen(false);
      setNewDeviceName('');

      if (result?.deviceCode) {
        // Kullaniciya kisa bir bilgi verelim; artik secret gosterimi yok.
        console.info(
          `[Pavo] Cihaz eklendi: ${trimmed} — Kod: ${result.deviceCode}`,
        );
      }

      await loadDevices();
    } catch (err: any) {
      console.error('Pavo cihazı ekleme hatası:', err);
      setError(err?.message || t('pavoDevicesTab.errors.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteDevice = async () => {
    if (!deletingDevice || !companyId) return;

    setDeleting(true);
    try {
      await pavoApiService.deletePavoDevice(deletingDevice.deviceCode, companyId);
      setDeleteDialogOpen(false);
      setDeletingDevice(null);
      await loadDevices();
    } catch (err: any) {
      console.error('Pavo cihazı silme hatası:', err);
      setError(err?.message || t('pavoDevicesTab.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusChip = (status?: string) => {
    switch (status) {
      case 'active':
        return <Chip icon={<CheckCircleIcon />} label={t('pavoDevicesTab.status.active')} color="success" size="small" />;
      case 'pending':
        return <Chip icon={<PendingIcon />} label={t('pavoDevicesTab.status.pendingSetup')} color="warning" size="small" />;
      case 'inactive':
        return <Chip icon={<CancelIcon />} label={t('pavoDevicesTab.status.passive')} color="error" size="small" />;
      default:
        return <Chip label={status || '-'} size="small" />;
    }
  };

  const getOnlineChip = (isOnline: boolean) => (
    <Chip
      icon={<OnlineDotIcon sx={{ fontSize: 10 }} />}
      label={isOnline ? t('pavoDevicesTab.online.online') : t('pavoDevicesTab.online.offline')}
      size="small"
      color={isOnline ? 'success' : 'default'}
      variant={isOnline ? 'filled' : 'outlined'}
    />
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const openAddDialog = () => {
    const suggested = screenName
      ? `${screenName} Pavo ${devices.length + 1}`
      : `${screenLabel} Pavo ${devices.length + 1}`;
    setNewDeviceName(suggested);
    setAddDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const canInteract = !!companyId && !!screenId;

  return (
    <Box>
      {/* Başlık */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <PointOfSaleIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t('pavoDevicesTab.title')}
          </Typography>
          <Chip label={t('pavoDevicesTab.deviceCount', { count: devices.length })} size="small" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('pavoDevicesTab.actions.refresh')}>
            <span>
              <IconButton onClick={loadDevices} size="small" disabled={!canInteract}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={openAddDialog}
            disabled={!canInteract}
          >
            {t('pavoDevicesTab.actions.addDevice')}
          </Button>
        </Box>
      </Box>

      {!canInteract && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('pavoDevicesTab.saveScreenSettingsHint')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Liste */}
      {devices.length === 0 && canInteract ? (
        <Paper
          sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}
        >
          <PointOfSaleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('pavoDevicesTab.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('pavoDevicesTab.empty.description', { screenLabel: screenLabel.toLowerCase() })}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
            {t('pavoDevicesTab.empty.addFirst')}
          </Button>
        </Paper>
      ) : devices.length > 0 ? (
        <>
          {!isMobile ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.deviceName')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.deviceCode')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.status')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.online')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.deviceModel')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                      {t('pavoDevicesTab.table.lastSeen')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}
                    >
                      {t('pavoDevicesTab.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.deviceCode} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {device.deviceName}
                        </Typography>
                        {device.pavoNumber != null && (
                          <Typography variant="caption" color="text.secondary">
                            Pavo #{device.pavoNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight="bold"
                            sx={{ letterSpacing: 1 }}
                          >
                            {device.deviceCode}
                          </Typography>
                          <Tooltip title={copiedCode === device.deviceCode ? t('pavoDevicesTab.actions.copied') : t('pavoDevicesTab.actions.copy')}>
                            <IconButton size="small" onClick={() => handleCopyCode(device.deviceCode)}>
                              <ContentCopyIcon
                                fontSize="small"
                                color={copiedCode === device.deviceCode ? 'success' : 'action'}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(device.status)}</TableCell>
                      <TableCell>{getOnlineChip(!!device.isOnline)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {device.deviceModel || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(device.lastSeen)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('pavoDevicesTab.actions.deleteDevice')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeletingDevice(device);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              {devices.map((device) => (
                <Card key={device.deviceCode} sx={{ mb: 1.5, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {device.deviceName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight="bold"
                            sx={{ letterSpacing: 1 }}
                          >
                            {device.deviceCode}
                          </Typography>
                          <IconButton size="small" onClick={() => handleCopyCode(device.deviceCode)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                        {getStatusChip(device.status)}
                        {getOnlineChip(!!device.isOnline)}
                      </Box>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pavoDevicesTab.table.deviceModel')}
                        </Typography>
                        <Typography variant="body2">{device.deviceModel || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pavoDevicesTab.table.lastSeen')}
                        </Typography>
                        <Typography variant="body2">{formatDate(device.lastSeen)}</Typography>
                      </Grid>
                    </Grid>
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => {
                          setDeletingDevice(device);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        {t('pavoDevicesTab.actions.delete')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      ) : null}

      {/* Ekleme Modalı */}
      <Dialog open={addDialogOpen} onClose={() => !adding && setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pavoDevicesTab.addDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pavoDevicesTab.addDialog.description')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('pavoDevicesTab.addDialog.deviceName')}
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
            sx={{ mt: 1 }}
            placeholder={`${t('pavoDevicesTab.addDialog.examplePrefix')}: ${screenLabel} Pavo 1`}
            disabled={adding}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit" disabled={adding}>
            {t('pavoDevicesTab.common.cancel')}
          </Button>
          <Button
            onClick={handleAddDevice}
            variant="contained"
            disabled={adding || !newDeviceName.trim()}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {adding ? t('pavoDevicesTab.actions.adding') : t('pavoDevicesTab.actions.addDevice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Modalı */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pavoDevicesTab.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('pavoDevicesTab.deleteDialog.description', { name: deletingDevice?.deviceName })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('pavoDevicesTab.table.deviceCode')}: <strong>{deletingDevice?.deviceCode}</strong>
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {t('pavoDevicesTab.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleting}>
            {t('pavoDevicesTab.common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteDevice}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('pavoDevicesTab.actions.deleting') : t('pavoDevicesTab.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PavoDevicesTab;
